/**
 * VIP Tiers dashboard — visualizes player tiers with summary cards and player details.
 * Shows La Corona, Diamante, Platino, and Oro tiers (skips Silver/Bronze).
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import { useSessionContext } from '../auth/SessionProvider.tsx';
import type { ApiFailure } from '../api/client.ts';
import {
  createVipTiersApi,
  type TierName,
  type TierSummary,
  type VipPlayer,
  type VipTiersResponse,
} from './vip-tiers-api.ts';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (amount: number): string => {
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatNumber = (n: number): string => new Intl.NumberFormat('en-US').format(n);

const formatPercent = (n: number): string => {
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}%`;
};

const formatDate = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('es-PR', { day: 'numeric', month: 'short', year: 'numeric' });
};

// ─── Tier Config ─────────────────────────────────────────────────────────────

interface TierConfig {
  readonly name: TierName;
  readonly label: string;
  readonly color: string;
  readonly bgColor: string;
  readonly borderColor: string;
  readonly textColor: string;
  readonly badgeColor: string;
  readonly icon: string;
}

const TIER_CONFIGS: Record<string, TierConfig> = {
  'La Corona': {
    name: 'La Corona',
    label: 'La Corona',
    color: '',
    bgColor: 'bg-secondary',
    borderColor: 'border-border',
    textColor: 'text-foreground',
    badgeColor: 'bg-secondary text-foreground ring-border',
    icon: '👑',
  },
  Diamante: {
    name: 'Diamante',
    label: 'Diamante',
    color: '',
    bgColor: 'bg-secondary',
    borderColor: 'border-border',
    textColor: 'text-foreground',
    badgeColor: 'bg-secondary text-foreground ring-border',
    icon: '💎',
  },
  Platino: {
    name: 'Platino',
    label: 'Platino',
    color: '',
    bgColor: 'bg-secondary',
    borderColor: 'border-border',
    textColor: 'text-foreground',
    badgeColor: 'bg-secondary text-foreground ring-border',
    icon: '🏆',
  },
  Oro: {
    name: 'Oro',
    label: 'Oro',
    color: '',
    bgColor: 'bg-secondary',
    borderColor: 'border-border',
    textColor: 'text-foreground',
    badgeColor: 'bg-secondary text-foreground ring-border',
    icon: '🥇',
  },
};

// ─── Components ──────────────────────────────────────────────────────────────

const TierSummaryCard = ({
  summary,
  config,
  totalDeposits,
}: {
  readonly summary: TierSummary;
  readonly config: TierConfig;
  readonly totalDeposits: number;
}): ReactElement => (
  <div
    className={`relative overflow-hidden rounded-xl border ${config.borderColor} ${config.bgColor} p-5 shadow-sm transition-shadow hover:shadow-md`}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{config.icon}</span>
          <h3 className={`font-display text-lg font-bold ${config.textColor}`}>{config.label}</h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {summary.playerCount} {summary.playerCount === 1 ? 'jugador' : 'jugadores'}
        </p>
      </div>
      <div className="text-right">
        <p className={`font-display text-xl font-bold ${config.textColor}`}>
          {formatCurrency(summary.totalDeposits)}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatPercent(totalDeposits > 0 ? (summary.totalDeposits / totalDeposits) * 100 : 0)} del
          total
        </p>
      </div>
    </div>
    <div className="mt-4 grid grid-cols-3 gap-3">
      <div>
        <p className="text-xs text-muted-foreground">Avg Depósito</p>
        <p className="font-semibold text-sm">{formatCurrency(summary.avgDeposit)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">NCC Total</p>
        <p
          className={`font-semibold text-sm ${summary.totalNCC >= 0 ? 'text-foreground' : 'text-destructive'}`}
        >
          {formatCurrency(summary.totalNCC)}
        </p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Avg NCC</p>
        <p
          className={`font-semibold text-sm ${summary.avgNCC >= 0 ? 'text-foreground' : 'text-destructive'}`}
        >
          {formatCurrency(summary.avgNCC)}
        </p>
      </div>
    </div>
  </div>
);

const PlayerStatusBadge = ({
  status,
  rgLocked,
}: {
  readonly status: string;
  readonly rgLocked: boolean;
}): ReactElement => {
  if (rgLocked) {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-border"
        style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }}
      >
        RG Locked
      </span>
    );
  }
  if (status === 'Enabled') {
    return (
      <span
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-border"
        style={{ backgroundColor: '#d1fae5', color: '#047857' }}
      >
        Activo
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground ring-1 ring-border">
      {status}
    </span>
  );
};

const DormancyIndicator = ({ days }: { readonly days: number }): ReactElement => {
  if (days === 0) {
    return <span className="text-xs text-foreground font-medium">Hoy</span>;
  }
  if (days <= 7) {
    return <span className="text-xs text-foreground">{days}d</span>;
  }
  if (days <= 30) {
    return <span className="text-xs text-muted-foreground">{days}d</span>;
  }
  return <span className="text-xs text-destructive font-medium">{days}d ⚠️</span>;
};

const ProgressToNextTier = ({
  player,
  config,
}: {
  readonly player: VipPlayer;
  readonly config: TierConfig;
}): ReactElement | null => {
  if (!player.nextTier || player.amountToNextTier === null) return null;
  const nextConfig = TIER_CONFIGS[player.nextTier];
  if (!nextConfig) return null;

  const currentTierMin =
    player.tier === 'La Corona'
      ? 250000
      : player.tier === 'Diamante'
        ? 100000
        : player.tier === 'Platino'
          ? 50000
          : 10000;
  const nextTierMin =
    player.nextTier === 'La Corona'
      ? 250000
      : player.nextTier === 'Diamante'
        ? 100000
        : player.nextTier === 'Platino'
          ? 50000
          : 10000;
  const range = nextTierMin - currentTierMin;
  const progress = range > 0 ? ((player.periodDepositAmount - currentTierMin) / range) * 100 : 0;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
        <span>
          {config.icon} {config.label}
        </span>
        <span>
          {nextConfig.icon} {nextConfig.label}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-secondary">
        <div
          className="h-1.5 rounded-full bg-primary"
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
      <p className="mt-1 text-xs text-muted-foreground text-right">
        Faltan {formatCurrency(player.amountToNextTier)}
      </p>
    </div>
  );
};

const PlayerCard = ({ player }: { readonly player: VipPlayer }): ReactElement => {
  const config = TIER_CONFIGS[player.tier]!;
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`rounded-lg border ${config.borderColor} bg-card p-4 shadow-sm transition-all hover:shadow-md`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full ${config.bgColor} ${config.borderColor} border`}
          >
            <span className="text-lg">{config.icon}</span>
          </div>
          <div>
            <p className="font-semibold text-sm">
              {player.firstName} {player.lastName}
            </p>
            <p className="text-xs text-muted-foreground">{player.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PlayerStatusBadge status={player.status} rgLocked={player.rgLocked} />
          <DormancyIndicator days={player.daysSinceDeposit} />
        </div>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Depósitos</p>
          <p className="font-bold text-sm">{formatCurrency(player.periodDepositAmount)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">NCC</p>
          <p
            className={`font-bold text-sm ${player.netCashContribution >= 0 ? 'text-foreground' : 'text-destructive'}`}
          >
            {formatCurrency(player.netCashContribution)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Apuestas</p>
          <p className="font-semibold text-sm">{formatNumber(player.numberOfBets)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Avg Bet</p>
          <p className="font-semibold text-sm">{formatCurrency(player.averageBet)}</p>
        </div>
      </div>

      <ProgressToNextTier player={player} config={config} />

      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {expanded ? '▲ Menos detalles' : '▼ Más detalles'}
      </button>

      {expanded && (
        <div className="mt-3 grid grid-cols-4 gap-3 border-t border-border pt-3">
          <div>
            <p className="text-xs text-muted-foreground">Margin</p>
            <p
              className={`text-xs font-medium ${player.margin >= 0 ? 'text-foreground' : 'text-destructive'}`}
            >
              {formatPercent(player.margin * 100)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Hold %</p>
            <p className="text-xs font-medium">{formatPercent(player.holdPercent)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Wager Cycle</p>
            <p className="text-xs font-medium">{player.wagerCycle.toFixed(2)}x</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Días Depositando</p>
            <p className="text-xs font-medium">{formatNumber(player.periodDepositingDays)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Stakes (Sports)</p>
            <p className="text-xs font-medium">{formatCurrency(player.sportsCashStakes)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Retiros</p>
            <p className="text-xs font-medium">{formatCurrency(player.totalWithdrawals)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Registrado</p>
            <p className="text-xs font-medium">{formatDate(player.registrationDate)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const TierSection = ({
  tierName: _tierName,
  players,
  config,
}: {
  readonly tierName: TierName;
  readonly players: readonly VipPlayer[];
  readonly config: TierConfig;
}): ReactElement => (
  <div className="space-y-3">
    <div className="flex items-center gap-2">
      <span className="text-xl">{config.icon}</span>
      <h3 className={`font-display text-lg font-bold ${config.textColor}`}>{config.label}</h3>
      <span
        className={`ml-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${config.badgeColor}`}
      >
        {players.length} {players.length === 1 ? 'jugador' : 'jugadores'}
      </span>
    </div>
    <div className="grid gap-3 sm:grid-cols-1 lg:grid-cols-2">
      {players.map((player) => (
        <PlayerCard key={player.userId} player={player} />
      ))}
    </div>
  </div>
);

// ─── Pyramid Visualization ───────────────────────────────────────────────────

const TierPyramid = ({
  summaries,
  totalDeposits,
}: {
  readonly summaries: readonly TierSummary[];
  readonly totalDeposits: number;
}): ReactElement => {
  const visibleTiers: TierName[] = ['La Corona', 'Diamante', 'Platino', 'Oro', 'Plata', 'Bronce'];

  return (
    <div className="flex flex-col items-center gap-1 py-4">
      {visibleTiers.map((tierName, idx) => {
        const summary = summaries.find((s) => s.tier === tierName);
        const config = TIER_CONFIGS[tierName];
        if (!summary) return null;

        // Width increases as you go down the pyramid
        const widthPercent = 30 + idx * 12;

        return (
          <div
            key={tierName}
            className="flex items-center justify-center gap-3 rounded-lg border border-border/50 px-4 py-2 transition-colors hover:bg-muted/50"
            style={{ width: `${widthPercent}%`, minWidth: '200px' }}
          >
            <span className="text-base">{config?.icon ?? ''}</span>
            <span className="text-sm font-semibold">{tierName}</span>
            <span className="text-xs text-muted-foreground">
              {summary.playerCount} ·{' '}
              {formatPercent(totalDeposits > 0 ? (summary.totalDeposits / totalDeposits) * 100 : 0)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── Reconnect Banner ──────────────────────────────────────────────────────────

/** The reconnect banner for an expired Snowflake token — the specific, recoverable failure, styled
 * like the owner report's so the two analytics surfaces answer a 503 the same way. */
const ReconnectBanner = ({ message }: { readonly message: string }): ReactElement => (
  <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4" role="alert">
    <p className="m-0 font-medium text-destructive">No se pudo conectar con Snowflake</p>
    <p className="m-0 mt-1 text-sm text-muted-foreground">{message}</p>
  </div>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

type ViewMode = 'cards' | 'table';

export const VipTiersPage = (): ReactElement => {
  const session = useSessionContext();
  const api = useMemo(() => createVipTiersApi(session.client), [session.client]);

  const [data, setData] = useState<VipTiersResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [selectedTier, setSelectedTier] = useState<TierName | 'all'>('all');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      setLoading(true);
      setFailure(null);
      const result = await api.getTiers(['La Corona', 'Diamante', 'Platino', 'Oro']);
      if (cancelled) return;
      if (result.ok) setData(result.data);
      else setFailure(result.failure);
      setLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [api, refreshKey]);

  const filteredPlayers = useMemo(() => {
    if (!data) return [];
    if (selectedTier === 'all') return data.players;
    return data.players.filter((p) => p.tier === selectedTier);
  }, [data, selectedTier]);

  const visibleSummaries = useMemo(() => {
    if (!data) return [];
    return data.summaries.filter((s) =>
      ['La Corona', 'Diamante', 'Platino', 'Oro'].includes(s.tier),
    );
  }, [data]);

  // Group players by tier for card view
  const playersByTier = useMemo(() => {
    const groups: Partial<Record<TierName, VipPlayer[]>> = {};
    for (const player of filteredPlayers) {
      if (!groups[player.tier]) groups[player.tier] = [];
      groups[player.tier]!.push(player);
    }
    return groups;
  }, [filteredPlayers]);

  if (loading && !data) {
    return (
      <section className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="mt-3 text-muted-foreground">Cargando niveles VIP...</p>
        </div>
      </section>
    );
  }

  if (failure) {
    // A 503 from the API is the Snowflake token being expired/unauthorised — the reconnect case,
    // shown as the same banner the owner report uses. Everything else keeps the generic error +
    // Retry.
    const reconnect = failure.kind === 'http' && failure.status === 503 ? failure.message : null;
    return (
      <section className="flex items-center justify-center py-20">
        <div className="w-full max-w-lg text-center">
          {reconnect !== null ? (
            <ReconnectBanner message={reconnect} />
          ) : (
            <p className="text-destructive font-medium">Error: {failure.message}</p>
          )}
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            Reintentar
          </button>
        </div>
      </section>
    );
  }

  if (!data) return <></>;

  return (
    <section aria-labelledby="vip-tiers-heading" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 id="vip-tiers-heading" className="font-display text-2xl font-bold">
            Niveles VIP
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {formatNumber(data.totalPlayers)} jugadores totales ·{' '}
            {formatCurrency(data.totalDeposits)} en depósitos
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setViewMode('cards')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'cards'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-secondary'
            }`}
          >
            Tarjetas
          </button>
          <button
            type="button"
            onClick={() => setViewMode('table')}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'table'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-secondary'
            }`}
          >
            Tabla
          </button>
        </div>
      </div>

      {/* Tier Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {visibleSummaries.map((summary) => {
          const config = TIER_CONFIGS[summary.tier];
          if (!config) return null;
          return (
            <TierSummaryCard
              key={summary.tier}
              summary={summary}
              config={config}
              totalDeposits={data.totalDeposits}
            />
          );
        })}
      </div>

      {/* Pyramid */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <h3 className="font-display text-sm font-semibold text-center text-muted-foreground uppercase tracking-wide mb-2">
          Distribución de Niveles
        </h3>
        <TierPyramid summaries={data.summaries} totalDeposits={data.totalDeposits} />
      </div>

      {/* Tier Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-muted-foreground">Filtrar:</span>
        <button
          type="button"
          onClick={() => setSelectedTier('all')}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            selectedTier === 'all'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-secondary'
          }`}
        >
          Todos
        </button>
        {(['La Corona', 'Diamante', 'Platino', 'Oro'] as const).map((tier) => {
          const config = TIER_CONFIGS[tier];
          if (!config) return null;
          return (
            <button
              key={tier}
              type="button"
              onClick={() => setSelectedTier(tier)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                selectedTier === tier
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-secondary'
              }`}
            >
              {config.icon} {config.label}
            </button>
          );
        })}
      </div>

      {/* Players */}
      {viewMode === 'cards' ? (
        <div className="space-y-8">
          {(['La Corona', 'Diamante', 'Platino', 'Oro'] as const).map((tierName) => {
            const players = playersByTier[tierName];
            const config = TIER_CONFIGS[tierName];
            if (!players || players.length === 0 || !config) return null;
            return (
              <TierSection key={tierName} tierName={tierName} players={players} config={config} />
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-3 py-2.5 text-left font-semibold">Jugador</th>
                <th className="px-3 py-2.5 text-left font-semibold">Nivel</th>
                <th className="px-3 py-2.5 text-right font-semibold">Depósitos</th>
                <th className="px-3 py-2.5 text-right font-semibold">NCC</th>
                <th className="px-3 py-2.5 text-right font-semibold">Apuestas</th>
                <th className="px-3 py-2.5 text-right font-semibold">Avg Bet</th>
                <th className="px-3 py-2.5 text-right font-semibold">Margin</th>
                <th className="px-3 py-2.5 text-right font-semibold">Hold %</th>
                <th className="px-3 py-2.5 text-right font-semibold">Wager Cycle</th>
                <th className="px-3 py-2.5 text-right font-semibold">Días Dep.</th>
                <th className="px-3 py-2.5 text-right font-semibold">Sports</th>
                <th className="px-3 py-2.5 text-right font-semibold">Retiros</th>
                <th className="px-3 py-2.5 text-center font-semibold">Status</th>
                <th className="px-3 py-2.5 text-right font-semibold">Última Act.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPlayers.map((player) => {
                const config = TIER_CONFIGS[player.tier];
                return (
                  <tr key={player.userId} className="hover:bg-muted/30 transition-colors">
                    <td className="px-3 py-2.5">
                      <div>
                        <p className="font-medium">
                          {player.firstName} {player.lastName}
                        </p>
                        <p className="text-xs text-muted-foreground">{player.email}</p>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${config?.badgeColor ?? ''}`}
                      >
                        {config?.icon} {player.tier}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold">
                      {formatCurrency(player.periodDepositAmount)}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right font-semibold ${player.netCashContribution >= 0 ? 'text-foreground' : 'text-destructive'}`}
                    >
                      {formatCurrency(player.netCashContribution)}
                    </td>
                    <td className="px-3 py-2.5 text-right">{formatNumber(player.numberOfBets)}</td>
                    <td className="px-3 py-2.5 text-right">{formatCurrency(player.averageBet)}</td>
                    <td
                      className={`px-3 py-2.5 text-right ${player.margin >= 0 ? 'text-foreground' : 'text-destructive'}`}
                    >
                      {formatPercent(player.margin * 100)}
                    </td>
                    <td className="px-3 py-2.5 text-right">{formatPercent(player.holdPercent)}</td>
                    <td className="px-3 py-2.5 text-right font-medium">
                      {player.wagerCycle.toFixed(2)}x
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {formatNumber(player.periodDepositingDays)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {formatCurrency(player.sportsCashStakes)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {formatCurrency(player.totalWithdrawals)}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <PlayerStatusBadge status={player.status} rgLocked={player.rgLocked} />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <DormancyIndicator days={player.daysSinceDeposit} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
